<template>
  <v-dialog class="modal" persistent width="700" v-model="internal">
    <v-card v-if="operation" class="elevation-0">
      <v-card-actions class="d-flex justify-space-between">
        <v-card-title>{{ $t('instructions.uploadFiles.documentation') }}&nbsp;<i>{{ operation.alias }}</i></v-card-title>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-actions>

      <v-stepper v-model="steps" class="elevation-0" style="background: none">
        <v-stepper-header class="px-4 elevation-0">
          <v-stepper-step
            v-for="i in totalSteps"
            :key="'step-' + i"
            :complete="steps > i"
            :step="i"
          ></v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <!-- upload files -->
          <v-stepper-content :step="1">
            <file-drop-upload
              v-model="stagedFiles"
              :max-size="maxOperationFileSize"
              @error="onUploadError"
              @remove="onStagedFileRemoved"
            />
          </v-stepper-content>

          <!-- per doc-type mapping -->
          <v-stepper-content :step="2">
            <div v-if="loadingExisting" class="d-flex justify-center my-6">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <template v-else>
              <v-alert v-if="!invoiceCovered" type="warning" dense outlined class="mb-3">
                {{ $t('instructions.uploadFiles.invoiceRequiredFirst') }}
              </v-alert>
            <v-card class="pa-4 elevation-0" outlined>
              <div
                v-for="([label, doctype], index) in docTypeEntries"
                :key="doctype"
                class="d-flex justify-space-between align-center my-2 flex-wrap doc-row"
              >
                <div class="d-flex flex-row align-center flex-wrap">
                  <h3 class="mr-3">{{ label }}</h3>
                  <v-chip outlined small color="red" class="mr-2" v-show="index === 0">
                    {{ $t('instructions.uploadFiles.obligatory') }}
                  </v-chip>
                  <v-chip v-if="existingStatus[doctype]" outlined small color="green">
                    <v-icon x-small class="mr-1">mdi-check</v-icon>
                    {{ $t('instructions.uploadFiles.alreadyUploaded') }}
                  </v-chip>
                  <v-btn
                    v-if="existingUrls[doctype]"
                    icon
                    x-small
                    class="ml-1"
                    :href="existingUrls[doctype]"
                    target="_blank"
                  >
                    <v-icon x-small>mdi-eye-outline</v-icon>
                  </v-btn>
                </div>

                <v-select
                  style="max-width: 220px"
                  v-model="selectedFilesInputs[doctype]"
                  :items="stagedFileNames"
                  :label="$t('instructions.uploadFiles.filesSelection')"
                  clearable
                  outlined
                  dense
                  hide-details
                />
              </div>
            </v-card>
            </template>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <div class="d-flex justify-end flex-gap-2 pa-5">
        <v-btn :disabled="steps <= 1" @click="steps--" class="elevation-0">
          {{ $t('instructions.back') }}
        </v-btn>
        <v-btn
          :disabled="handleDisabledButton"
          :loading="submitting"
          @click="nextPage"
          color="primary"
          class="elevation-0"
        >
          {{ steps < totalSteps ? $t('instructions.next') : $t('instructions.save') }}
        </v-btn>
      </div>

      <v-snackbar v-model="snackbar.show" :timeout="4000" :color="snackbar.color" bottom>
        <div class="black--text text-center w-100">{{ snackbar.message }}</div>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { useExecutionStore, MAX_OPERATION_FILE_SIZE } from '@/store'
import { mapStores } from 'pinia'
import { nomalizeFileTitle } from '@/utils'
import FileDropUpload, { selectPdfFiles } from '@/components/common/FileDropUpload.vue'

const DOC_TYPES = ['invoice', 'import_declaration', 'proof_of_import', 'bill_of_lading']
const MAX_STAGED_FILES = 4

export default {
  name: 'EditOperationFilesModal',
  components: { FileDropUpload },
  props: {
    value: { type: Boolean, default: false },
    operation: { type: Object, default: null },
    initialFiles: { type: Array, default: () => [] }
  },
  data () {
    return {
      steps: 1,
      totalSteps: 2,
      stagedFiles: [],
      selectedFilesInputs: {},
      existingStatus: {},
      existingUrls: {},
      loadingExisting: false,
      submitting: false,
      snackbar: { show: false, color: 'success', message: '' }
    }
  },
  computed: {
    ...mapStores(useExecutionStore),
    maxOperationFileSize () {
      return MAX_OPERATION_FILE_SIZE
    },
    internal: {
      get () { return this.value },
      set (v) { this.$emit('input', v) }
    },
    docTypeEntries () {
      const labels = Object.values(this.$t('instructions.uploadFiles.typesOfDocumentation'))
      return DOC_TYPES.map((doctype, index) => [labels[index], doctype])
    },
    stagedFileNames () {
      return this.stagedFiles.map(f => f.name)
    },
    invoiceCovered () {
      return Boolean(this.existingStatus.invoice || this.selectedFilesInputs.invoice)
    },
    canSubmit () {
      const hasAssignment = DOC_TYPES.some(doctype => this.selectedFilesInputs[doctype])
      if (!hasAssignment) return false
      const assigningNonInvoice = DOC_TYPES.some(doctype => doctype !== 'invoice' && this.selectedFilesInputs[doctype])
      // Backend requires an invoice on file before accepting any other doctype.
      return !assigningNonInvoice || this.invoiceCovered
    },
    handleDisabledButton () {
      if (this.steps === 1) return this.stagedFiles.length === 0
      return !this.canSubmit
    }
  },
  watch: {
    value (val) {
      if (val) this.open()
      else this.releaseExistingUrls()
    }
  },
  methods: {
    open () {
      this.steps = 1
      const { files } = selectPdfFiles({
        existingFiles: [],
        incoming: this.initialFiles,
        maxFiles: MAX_STAGED_FILES,
        maxSize: MAX_OPERATION_FILE_SIZE
      })
      this.stagedFiles = files
      this.selectedFilesInputs = Object.fromEntries(DOC_TYPES.map(d => [d, null]))
      this.releaseExistingUrls()
      this.existingStatus = {}
      this.existingUrls = {}
      this.loadExistingDocs()
    },
    async loadExistingDocs () {
      if (!this.operation?.id) return
      this.loadingExisting = true
      const results = await Promise.allSettled(
        DOC_TYPES.map(doctype => this.executionStore.getOperation(this.operation.id, doctype))
      )
      results.forEach((result, index) => {
        const doctype = DOC_TYPES[index]
        if (result.status === 'fulfilled') {
          this.$set(this.existingStatus, doctype, true)
          this.$set(this.existingUrls, doctype, window.URL.createObjectURL(result.value.data))
        } else {
          const status = result.reason?.response?.status
          if (status && status !== 404) {
            console.error(`Failed to check existing document for ${doctype} (operation ${this.operation.id}):`, result.reason)
          }
        }
      })
      this.loadingExisting = false
    },
    releaseExistingUrls () {
      Object.values(this.existingUrls).forEach(url => window.URL.revokeObjectURL(url))
    },
    onUploadError (message) {
      this.showSnackbar('error', message)
    },
    onStagedFileRemoved (removed) {
      DOC_TYPES.forEach(doctype => {
        if (this.selectedFilesInputs[doctype] === removed.name) this.selectedFilesInputs[doctype] = null
      })
    },
    showSnackbar (color, message) {
      this.snackbar = { show: true, color, message }
    },
    nextPage () {
      if (this.steps < this.totalSteps) {
        this.steps++
        return
      }
      this.submit()
    },
    async submit () {
      const assignments = DOC_TYPES
        .map(doctype => [doctype, this.selectedFilesInputs[doctype]])
        .filter(([, fileName]) => fileName)

      if (!assignments.length) return

      this.submitting = true
      const failedDoctypes = []

      for (const [doctype, fileName] of assignments) {
        const file = this.stagedFiles.find(f => f.name === fileName)
        if (!file) continue
        try {
          await this.executionStore.uploadOperation(
            this.operation.alias,
            doctype,
            nomalizeFileTitle(file),
            Boolean(this.operation.amlCheck),
            this.operation.id,
            this.operation.companyId,
            this.operation.operationType
          )
        } catch (error) {
          console.error('Failed to upload operation document', doctype, error)
          failedDoctypes.push(doctype)
        }
      }

      this.submitting = false

      if (failedDoctypes.length) {
        this.showSnackbar('error', this.$t('instructions.uploadFiles.messages.requestFailed'))
        return
      }

      this.showSnackbar('success', this.$t('broker.snackBar.filesUploaded'))
      this.$emit('updated')
      setTimeout(() => this.close(), 600)
    },
    close () {
      this.$emit('input', false)
    }
  }
}
</script>

<style scoped>
.doc-row {
  gap: .5rem;
}
.flex-gap-2 {
  gap: 8px;
}
</style>
